export interface ModelDetailedResultPayload {
  id?: number;
  servicesToEmployeeId?: number;
  case?: number;
  incomeDetailsSummaryId?: number;
  incomeDdffTableId?: string;
  incomeDdffTableName?: string;
  assetSubtypes?: number;
  periodTypes?: number;
  fromDate?: string;
  toDate?: string;
  devoDate?: string;
  days?: number;
  taxableBase?: number;
  taxRatePercentage?: number;
  fullAmountDue?: number;
  deductionDonation?: number;
  amountDueLaw?: number;
  convenioPercentage?: number;
  convenioLimit?: number;
  convenioReduction?: number;
  reducedAmountDue?: number;
  withholdingPayments?: number;
  previousDepositRefund?: number;
  resultSelfAssessment?: number;
  totalIncome?: number;
  deductibleExpenses?: number;
  taxableGeneral?: number;
  taxableCapitalGainProperty?: number;
  creationDate?: string;
  selfAssessPersonRepresentativeId?: number;
  modelBankAccountInformationId?: number | null;
  modelDomicilesInformationId?: number;
  nrc?: string;
  grouping?: boolean;
  supplementary?: string;
  justificantNumber?: string;
  formatPayTypeId?: number;
  declarationType?: number;
  countryResidentCode?: string;
  countryExemption?: string;
  convenioExemption?: string;
  acquisitionDate?: string;
  modelPayerCapitalGainRealState?: number;
  modelKey?: string;
  formDetails?: string;
  modelDetailedResultStatus?: string;
  devoRenuncia?: string;
}

function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toOptionalString(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const parsed = String(value);
  return parsed;
}

function toStatusName(value: unknown): string | undefined {
  if (value && typeof value === 'object' && 'nameEn' in (value as Record<string, unknown>)) {
    const nameEn = (value as Record<string, unknown>).nameEn;
    return toOptionalString(nameEn);
  }
  return toOptionalString(value);
}

function toPeriodTypeId(value: unknown): number | undefined {
  if (value && typeof value === 'object' && 'id' in (value as Record<string, unknown>)) {
    return toNumber((value as Record<string, unknown>).id);
  }
  return toNumber(value);
}

export function buildModelDetailedResultPayload(input: any): ModelDetailedResultPayload {
  const payload: ModelDetailedResultPayload = {};
  const mutablePayload = payload as Record<string, unknown>;

  const numericFields: Array<keyof ModelDetailedResultPayload> = [
    'id',
    'servicesToEmployeeId',
    'case',
    'incomeDetailsSummaryId',
    'assetSubtypes',
    'days',
    'taxableBase',
    'taxRatePercentage',
    'fullAmountDue',
    'deductionDonation',
    'amountDueLaw',
    'convenioPercentage',
    'convenioLimit',
    'convenioReduction',
    'reducedAmountDue',
    'withholdingPayments',
    'previousDepositRefund',
    'resultSelfAssessment',
    'totalIncome',
    'deductibleExpenses',
    'taxableGeneral',
    'taxableCapitalGainProperty',
    'selfAssessPersonRepresentativeId',
    'modelBankAccountInformationId',
    'modelDomicilesInformationId',
    'formatPayTypeId',
    'declarationType',
    'modelPayerCapitalGainRealState',
  ];

  for (const field of numericFields) {
    if (field === 'modelBankAccountInformationId' && input?.[field] === null) {
      payload.modelBankAccountInformationId = null;
      continue;
    }

    const value = toNumber(input?.[field]);
    if (value !== undefined) {
      mutablePayload[field] = value;
    }
  }

  const periodTypeId = toPeriodTypeId(input?.periodTypes);
  if (periodTypeId !== undefined) {
    payload.periodTypes = periodTypeId;
  }

  const status = toStatusName(input?.modelDetailedResultStatus);
  if (status !== undefined) {
    payload.modelDetailedResultStatus = status;
  }

  const stringFields: Array<keyof ModelDetailedResultPayload> = [
    'incomeDdffTableName',
    'incomeDdffTableId',
    'fromDate',
    'toDate',
    'devoDate',
    'creationDate',
    'nrc',
    'supplementary',
    'justificantNumber',
    'countryResidentCode',
    'countryExemption',
    'convenioExemption',
    'acquisitionDate',
    'modelKey',
    'formDetails',
    'devoRenuncia',
  ];

  for (const field of stringFields) {
    const value = toOptionalString(input?.[field]);
    if (value !== undefined) {
      mutablePayload[field] = value;
    }
  }

  if (typeof input?.grouping === 'boolean') {
    payload.grouping = input.grouping;
  }

  return payload;
}
